import {Component, DestroyRef, effect, inject, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {PageEvent} from '@angular/material/paginator';
import {catchError, finalize, map, of, Subject, switchMap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BREAKDOWN_COMPARISON_TITLE,
  CampaignFilters,
  CampaignFiltersResponse,
  CampaignOverviewRequest,
  CampaignOverviewResponse,
  CHARTS_COLOR_SCALE,
  ContentCategoryBreakdownRequest,
  ContentCategoryBreakdownResponse,
  CsvOption,
  DOWNLOAD_CSV_OPTIONS,
  InventoryQualityCsvResponse,
  InventoryQualityResponse,
  isSelectedFilters,
  RefreshRateDistributionRequest,
  RefreshRateDistributionResponse,
  SelectedFilters,
  SitesPerformanceRequest} from './types';
import {CustomValidators} from '../../shared/validators';
import {StateService} from '../../services/state.service';
import {CampaignService} from './campaign.service';
import {Format, UtilService} from '../../services/util.service';
import { ComparisonMetrics, InventoryQualityTableColumns, PerformanceComparisonMetric, SitePerformanceResponse } from '../my-brand-screen/types';
import { InventoryQualityRequest, SortFieldType } from './types';
import {SitesTableComponent} from '../my-brand-screen/sites-table/sites-table.component';
import {daysDataShowing, FILTER_BY_WEBSITE, ROWS_PER_PAGE} from '../../utils/constants';
import {PaginatorComponent} from '../../shared/components/paginator/paginator.component';
import {NoWidgetDataComponent} from '../my-brand-screen/no-widget-data/no-widget-data.component';
import {RefreshRateDistributionComponent} from '../../shared/components/charts/refresh-rate-distribution/refresh-rate-distribution.component';
import {SitePerformanceComponent} from '../my-brand-screen/site-performance/site-performance.component';
import {HorizontalBarChartComponent} from '../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import {WidgetContainerComponent} from '../my-brand-screen/widget-container/widget-container.component';
import {OverviewTilesComponent} from '../../shared/components/overview-tiles/overview-tiles.component';
import {filterValid} from '../../utils/validators';
import {PosthogService} from '../../services/posthog.service';
import {PosthogEventName, PosthogEventCode} from '../../services/types';
import {SearchBarComponent} from '../../shared/components/search-bar/search-bar.component';
import {SortClickEvent, SortDirection, defaultSortOptions, SortOptions} from '../../utils/sort-state';
import {isSortField} from './types';
import {DownloadWidgetComponent} from '../../shared/components/download-widget/download-widget.component';
import {SelectItem, SelectMenuComponent, SelectMode} from '../../shared/components/select-menu/select-menu.component';
import { DashboardType, TopBarWrapperComponent } from '../top-bar-wrapper/top-bar-wrapper.component';
import { DashboardService } from '../../services/dashboard.service';
import {TopLevelMetricsComponent} from '../brands-dashboard-screen/brands-dashboard/top-level-metrics/top-level-metrics.component';
import { tooltips } from '../../utils/tooltips';
import { DataTestIds } from '../../../../e2e/types';
import {EmptyCampaignsBannerComponent} from './empty-campaigns-banner/empty-campaigns-banner.component';
import {RibbonComponent} from './ribbon/ribbon.component';
import {StorageService} from '../../services/storage.service';
import {LocalStorageKeys} from '../../shared/types/storage';

@Component({
  selector: 'app-my-campaigns-screen',
  standalone: true,
  imports: [
    CommonModule,
    SelectMenuComponent,
    OverviewTilesComponent,
    WidgetContainerComponent,
    HorizontalBarChartComponent,
    TopBarWrapperComponent,
    SitePerformanceComponent,
    RefreshRateDistributionComponent,
    DownloadWidgetComponent,
    SearchBarComponent,
    SitesTableComponent,
    PaginatorComponent,
    NoWidgetDataComponent,
    TopLevelMetricsComponent,
    EmptyCampaignsBannerComponent,
    RibbonComponent,
  ],
  templateUrl: './my-campaigns-screen.component.html',
  styleUrl: './my-campaigns-screen.component.scss'
})
export class MyCampaignsScreenComponent {

  protected readonly daysDataShowing = daysDataShowing;
  protected readonly tooltips = tooltips.myCampaigns;
  protected readonly campaignBreakdownComparisonTitle = BREAKDOWN_COMPARISON_TITLE;
  protected readonly ATTENTION_COLOR_SCALE = CHARTS_COLOR_SCALE;
  protected readonly downloadCsvOptions = DOWNLOAD_CSV_OPTIONS;
  protected readonly DashboardType = DashboardType;
  protected readonly SelectMode = SelectMode;
  protected readonly comparisonMetricOptions = ComparisonMetrics;
  protected readonly DataTestIds = DataTestIds;

  public loadingState = {
    overview: true,
    contentCategoryBreakdown: true,
    lowestSites: true,
    refreshRateDistribution: true,
    inventoryQuality: true,
  };
  public inventoryQualityPageSize: number = 5;
  public inventoryQualityWebsiteSearchTerm: FormControl<string> = new FormControl('') as FormControl<string>;
  public inventoryQualityWebsiteSearch = false;

  public campaignControl: FormControl<string[]> = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [CustomValidators.minMaxElements(1)]
  });
  public campaignOptions: SelectItem[] = [];

  protected creativesControl = new FormControl<string[]>([], {nonNullable:true});
  protected creativesOptions: SelectItem<string>[] = [];

  protected categoriesControl = new FormControl<string[]>([], {nonNullable: true});
  protected categoriesOptions: SelectItem<string>[] = [];

  public breakdownMetricOptions: SelectItem<string>[] = ComparisonMetrics;
  public breakdownMetricControl: FormControl<String | null> = new FormControl(ComparisonMetrics[0].key);
  public format: Format = 'shortPercentage';

  protected overview?: CampaignOverviewResponse;
  protected contentCategoryBreakdownValue?: ContentCategoryBreakdownResponse;
  protected lowestSites?: SitePerformanceResponse;
  protected refreshDistribution?: RefreshRateDistributionResponse;
  protected inventoryQuality?: InventoryQualityResponse;

  protected readonly filterByWebsite = FILTER_BY_WEBSITE;
  protected readonly rowsPerPage = ROWS_PER_PAGE;
  protected readonly inventoryInitialPage = 1;
  protected inventoryCurrentPage = 1;

  private overviewData$ = new Subject<CampaignOverviewRequest>();
  private contentCategoryBreakdownData$ = new Subject<ContentCategoryBreakdownRequest>();
  private lowestSitesData$ = new Subject<SitesPerformanceRequest>();
  private refreshRateDistributionData$ = new Subject<RefreshRateDistributionRequest>();
  private inventoryQualityData$ = new Subject<InventoryQualityRequest>();

  private filters?: CampaignFilters;
  private inventoryQualitySortOptions: SortOptions = {
    sortField: InventoryQualityTableColumns.BRAND_IMPRESSIONS,
    isAsc: false
  };

  private campaignService = inject(CampaignService);
  private stateService = inject(StateService);
  private dashboardService = inject(DashboardService);
  private utilService = inject(UtilService);
  private storageService = inject(StorageService);
  private destroyRef = inject(DestroyRef);
  private posthogService = inject(PosthogService);

  private account = this.stateService.activeAccount;
  private accountId = this.account() || "hubdemo";


  @ViewChild('inventoryQualityPaginator') inventoryQualityPaginator!: PaginatorComponent;

  constructor() {
    this.InitSubjects();
    effect(() => this.loadCampaignData(true))
  }

  private InitSubjects(): void {
    this.initOverviewSubject();
    this.initContentCategoryBreakdownSubject();
    this.initLowestSitesSubject();
    this.initRefreshRateDistributionSubject();
    this.initInventoryQualitySubject();
  }

  private loadCampaignData(forced?: boolean, offset: number = 0): void {
    const storedFilters = this.getStoredFilters();
    const filters$ = this.filters && !forced ?
      of(this.filters) :
      this.campaignService.getCampaignFilters({
        accountKeys: [this.account()],
        ...(this.dashboardService.getDashboardFiltersPayload())
      })
        .pipe(
          map((response: CampaignFiltersResponse) => ({
            campaigns: Object.values(response).flatMap(item => item.campaigns || [])
          }))
        );

    filters$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((filters) => {
          this.filters = filters;
          if (!this.filters?.campaigns.length){
            this.accountId = 'hubdemo';
          }
          this.populateAllOptions(storedFilters, this.getSelectedFilters());
          this.setControlValuesFromFilters(storedFilters);
          const filtersPayload = this.dashboardService.getDashboardFiltersPayload();
          const payloadBase = {
            accountKeys: [this.accountId],
            campaignIds: this.campaignControl.value!,
            creativeIds: this.creativesControl.value!,
            ...filtersPayload
          };

          this.overviewData$.next(payloadBase);
          this.contentCategoryBreakdownData$.next({ ...payloadBase, metric: this.breakdownMetricControl.value as string });
          this.lowestSitesData$.next(payloadBase);
          this.refreshRateDistributionData$.next(payloadBase);
          this.inventoryQualityData$.next({
            ...payloadBase,
            creativeIds: this.creativesControl.value!,
            categories: this.categoriesControl.value!,
            sortField: this.inventoryQualitySortOptions.sortField as SortFieldType,
            isAsc: this.inventoryQualitySortOptions.isAsc,
            limit: this.inventoryQualityPageSize,
            ...(offset ? {offset} : {}),
            ...(this.inventoryQualityWebsiteSearchTerm.value ? {website: this.inventoryQualityWebsiteSearchTerm.value} : {}),
          });

          return of(null);
        }),
        catchError((error) => {
          console.error('Error loading campaign data:', error);
          return of();
        })
      )
      .subscribe();
  }

  private reloadData(offset: number = 0): void {
    const filtersPayload = this.dashboardService.getDashboardFiltersPayload();
    const payloadBase = {
      accountKeys: [this.accountId],
      campaignIds: this.campaignControl.value!,
      creativeIds: this.creativesControl.value!,
      ...filtersPayload
    };

    this.overviewData$.next(payloadBase);
    this.contentCategoryBreakdownData$.next({ ...payloadBase, metric: this.breakdownMetricControl.value as string });
    this.lowestSitesData$.next(payloadBase);
    this.refreshRateDistributionData$.next(payloadBase);
    this.inventoryQualityData$.next({
      ...payloadBase,
      creativeIds: this.creativesControl.value!,
      categories: this.categoriesControl.value!,
      sortField: this.inventoryQualitySortOptions.sortField as SortFieldType,
      isAsc: this.inventoryQualitySortOptions.isAsc,
      limit: this.inventoryQualityPageSize,
      ...(offset ? {offset} : {}),
      ...(this.inventoryQualityWebsiteSearchTerm.value ? {website: this.inventoryQualityWebsiteSearchTerm.value} : {}),
    });
  }

  private initOverviewSubject(): void {
    this.overviewData$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((payload: CampaignOverviewRequest) => {
          this.loadingState.overview = true;
          return this.campaignService.getCampaignOverview(payload).pipe(
            finalize(() => (this.loadingState.overview = false)),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.overview = result;
      });
  }

  private initContentCategoryBreakdownSubject(): void {
    this.contentCategoryBreakdownData$
      .pipe(
        switchMap((payload: ContentCategoryBreakdownRequest) => {
          this.loadingState.contentCategoryBreakdown = true;
          return this.campaignService.getContentCategoryBreakdown(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.loadingState.contentCategoryBreakdown = false)),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.contentCategoryBreakdownValue = result;
      });
  }

  private initLowestSitesSubject(): void {
    this.lowestSitesData$
      .pipe(
        switchMap((payload: SitesPerformanceRequest) => {
          this.loadingState.lowestSites = true;
          return this.campaignService.getLowestPerformingSites(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.loadingState.lowestSites = false)),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.lowestSites = result;
      });
  }

  private initRefreshRateDistributionSubject(): void {
    this.refreshRateDistributionData$
      .pipe(
        switchMap((payload: RefreshRateDistributionRequest) => {
          this.loadingState.refreshRateDistribution = true;
          return this.campaignService.getRefreshRateDistribution(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.loadingState.refreshRateDistribution = false)),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.refreshDistribution = result;
      });
  }

  private initInventoryQualitySubject(): void {
    this.inventoryQualityData$
      .pipe(
        switchMap((payload: InventoryQualityRequest) => {
          this.loadingState.inventoryQuality = true;
          return this.campaignService.getInventoryQuality(payload).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.loadingState.inventoryQuality = false)),
            catchError(() => of(undefined))
          );
        })
      )
      .subscribe(result => {
        if (result) this.inventoryQuality = result;
      });
  }

  private setControlValuesFromFilters(filters: SelectedFilters): void {
    if (filters) {
      this.campaignControl.setValue(filterValid<string>(filters.campaignIds, this.campaignOptions));
      this.creativesControl.setValue(filterValid<string>(filters.creativeIds, this.creativesOptions));
      this.categoriesControl.setValue(filterValid<string>(filters.categories, this.categoriesOptions));
    } else {
      this.campaignControl.setValue(this.campaignOptions.map(o => o.key));
      this.creativesControl.setValue(this.creativesOptions.map(o => o.key));
      this.categoriesControl.setValue(this.categoriesOptions.map(o => o.key));
    }
  }

  public onCampaignApply(): void {
    this.categoriesControl.enable();
    this.inventoryQualityWebsiteSearchTerm.reset();
    this.inventoryQualityWebsiteSearch = false;
    this.saveFiltersToLocalStorage();
    this.loadCampaignData();
  }

  public onCreativeApply(): void {
    this.reloadData();
    this.updateCategoryOptionsFromCreatives();
  }


  public onBreakdownMetricChange(): void {
    const accountKeys = [this.accountId];
    const filters = this.dashboardService.getDashboardFiltersPayload();
    this.contentCategoryBreakdownData$.next({
      accountKeys: accountKeys,
      creativeIds: this.creativesControl.value!,
      campaignIds: this.campaignControl.value!,
      metric: this.breakdownMetricControl.value as string,
      ...filters
    });
    this.format = this.getBreakdownChartFormat();
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, {
      [PosthogEventCode.WIDGET_NAME]: BREAKDOWN_COMPARISON_TITLE,
      [PosthogEventCode.METRIC_NAME]: this.breakdownMetricControl.value as string
    });
  }

  public onApplyInventoryQualityCategories(): void {
    this.posthogService.capture(PosthogEventName.FILTERED_CONTENT_CATEGORY, {
      [PosthogEventCode.FILTERED_CONTENT_CATEGORY]: this.categoriesControl.value?.join(';'),
      [PosthogEventCode.WIDGET_NAME]: 'inventory_quality_table'
    });
    this.updateCreativesOptionsFromCategories();
    this.saveFiltersToLocalStorage();
    this.getInventoryQualityData();
  }

  public onInventoryQualityPageChange(event: PageEvent): void {
    this.inventoryCurrentPage = event.pageIndex + 1;
    const offset = event.pageIndex * this.inventoryQualityPageSize;
    this.saveFiltersToLocalStorage();
    this.getInventoryQualityData(offset);
    this.posthogService.capture(PosthogEventName.INVENTORY_QUALITY_TABLE_PAGINATION, {
      pageNumber: event.pageIndex + 1,
      lastPageNumber: Math.ceil(event.length / event.pageSize),
    });
  }

  public boundDownloadHandler = this.onDownloadInventoryQualityCsv.bind(this);

  public async onDownloadInventoryQualityCsv(option: CsvOption): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const reportType = option === CsvOption.DAILY_BREAKDOWN ? 'daily_breakdown' : 'summary_view';
      this.posthogService.capture(PosthogEventName.TABLE_EXPORTED, {
        [PosthogEventCode.REPORT_TYPE]: reportType,
        [PosthogEventCode.WIDGET_NAME]: 'inventory_quality_table'
      });
      const result = await this.getInventoryQualityCsv(option === CsvOption.DAILY_BREAKDOWN);
      await this.utilService.downloadFileFromUrl(result.fileUrl);
      return resolve();
    });
  }

  private getBreakdownChartFormat(): Format {
    switch (this.breakdownMetricControl.value) {
      case PerformanceComparisonMetric.ATTENTION:
        return 'shortPercentage';
      case PerformanceComparisonMetric.VIEWABILITY:
        return 'shortPercentage';
      case PerformanceComparisonMetric.DWELL_TIME:
        return 'seconds';
      case PerformanceComparisonMetric.CPM:
        return 'currency';
      case PerformanceComparisonMetric.AD_CLUTTER:
        return 'shortNumber';
    }
    return 'shortNumber';
  }

  public onInventoryQualityRowsChange(rowsPerPage: number | null) {
    if (!rowsPerPage) return;
    this.inventoryQualityPageSize = rowsPerPage;
    this.inventoryQualityPaginator.goToPage(1);
    this.getInventoryQualityData();
  }

  public onInventoryQualityWebsiteSearch(): void {
    this.inventoryQualityWebsiteSearch = true;
    this.inventoryQualityWebsiteSearchTerm.value ? this.categoriesControl.disable() : this.categoriesControl.enable();
    this.inventoryQualityPaginator?.goToPage(1);
    const includeCategories = this.inventoryQualityWebsiteSearchTerm.value ? false : true;
    this.getInventoryQualityData(0, includeCategories);
  }

  private updateCategoryOptionsFromCreatives(): void {
    if (this.inventoryQualityWebsiteSearch) return;

    const selectedCreativeIds = new Set(this.getSelectedFilters().creativeIds ?? []);
    const categorySet = new Set<string>();

    for (const campaign of this.filters?.campaigns ?? []) {
      for (const creative of campaign.creatives) {
        if (!selectedCreativeIds.size || selectedCreativeIds.has(creative.id)) {
          for (const category of creative.categories) {
            if (!categorySet.has(category)) {
              this.categoriesOptions.push({key: category, label: category});
              categorySet.add(category);
            }
          }
        }
      }
    }

    this.categoriesOptions = Array.from(categorySet).sort().map(c => ({key: c, label: c}));
    this.categoriesControl.setValue(this.categoriesOptions.map(o => o.key));
  }

  private updateCreativesOptionsFromCategories(): void {
    const selectedCampaignIds = new Set(this.getSelectedFilters().campaignIds);
    const selectedCategories = new Set(this.categoriesControl.value ?? []);
    const creativeSet = new Set<string>();

    for (const campaign of this.filters?.campaigns ?? []) {
      for (const creative of campaign.creatives) {
        if (selectedCampaignIds.has(campaign.id) || !selectedCampaignIds.size) {
          if (creative.categories.some(category => selectedCategories.has(category)) || !selectedCategories.size) {
            if (!creativeSet.has(creative.id)) {
              creativeSet.add(creative.id);
            }
          }
        }
      }
    }

    this.creativesOptions = Array.from(creativeSet).sort().map(c => ({key: c, label: c}));
    this.creativesControl.setValue(this.creativesOptions.map(o => o.key));
  }

  private populateAllOptions(storedFilters: SelectedFilters, selectedFilters: SelectedFilters): void {
    if (!this.filters) return;

    const selectedCampaignIds = selectedFilters.campaignIds.length ? new Set(selectedFilters.campaignIds) : new Set(storedFilters.campaignIds);

    const creativeSet = new Set<string>();
    const categorySet = new Set<string>();

    this.campaignOptions = this.filters.campaigns.map(c => ({key: c.id, label: c.id}));
    this.creativesOptions = [];
    this.categoriesOptions = [];

    this.filters.campaigns.forEach(campaign => {
      if (selectedCampaignIds.has(campaign.id) || !selectedCampaignIds.size) {
        campaign.creatives.forEach(creative => {
          if (!creativeSet.has(creative.id)) {
            creativeSet.add(creative.id);
          }
          creative.categories.forEach(category => {
            if (!categorySet.has(category)) {
              categorySet.add(category);
            }
          });
        });
      }
    });

    this.campaignOptions = this.filters.campaigns.map(c => ({key: c.id, label: c.id}));
    this.creativesOptions = Array.from(creativeSet).sort().map(c => ({key: c, label: c}));
    this.categoriesOptions = Array.from(categorySet).sort().map(c => ({key: c, label: c}));
  }

  private getSelectedFilters(): SelectedFilters {
    return {
      campaignIds: this.campaignControl.value ?? [],
      creativeIds: this.creativesControl.value ?? [],
      categories: this.categoriesControl.value ?? []
    };
  }

  private getStoredFilters(): SelectedFilters {
    const filters = this.storageService.local.getItem(LocalStorageKeys.CAMPAIGN_FILTERS) ?? {};
    return isSelectedFilters(filters) ? filters : {campaignIds: [], creativeIds: [], categories: []};
  }

  private saveFiltersToLocalStorage(): void {
    let filters: SelectedFilters = this.getSelectedFilters();
    // reset creativeIds and categories
    filters.categories = [];
    filters.creativeIds = [];

    this.storageService.local.setItem(LocalStorageKeys.CAMPAIGN_FILTERS, filters);
  }

  private getInventoryQualityCsv(includeDate: boolean): Promise<InventoryQualityCsvResponse> {
    return new Promise<InventoryQualityCsvResponse>((resolve, reject) => {
      const accountKeys = [this.accountId];
      const filters = this.getSelectedFilters();
      this.campaignService.getInventoryQualityCsv({
        campaignIds: filters.campaignIds,
        creativeIds: filters.creativeIds,
        categories: filters.categories,
        accountKeys: accountKeys,
        date: includeDate,
        ...this.dashboardService.getDashboardFiltersPayload()
      })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
          }))
        .subscribe((result) => {
          return resolve(result);
        });
    });
  }

  private getInventoryQualityData(offset: number = 0, includeCategories: boolean = true): void {
    const accountKeys = [this.accountId];
    const filters = this.getSelectedFilters();
    this.inventoryQualityData$.next({
      campaignIds: filters.campaignIds,
      creativeIds: filters.creativeIds,
      categories: includeCategories ? filters.categories : [],
      accountKeys: accountKeys,
      offset,
      limit: this.inventoryQualityPageSize,
      ...(this.inventoryQualityWebsiteSearchTerm.value ? {website: this.inventoryQualityWebsiteSearchTerm.value} : {}),
      sortField: this.inventoryQualitySortOptions.sortField as SortFieldType,
      isAsc: this.inventoryQualitySortOptions.isAsc,
      ...(this.dashboardService.getDashboardFiltersPayload())
    });
  }

  protected onSortInventoryQualityTable(event: SortClickEvent): void {
    if (event.direction !== SortDirection.default && isSortField(event.field)) {
      this.inventoryQualitySortOptions = {
        sortField: event.field,
        isAsc: event.direction === SortDirection.asc
      }
    } else {
      this.inventoryQualitySortOptions = defaultSortOptions;
    }

    this.inventoryQualityPaginator?.goToPage(1);
    const includeCategories = this.inventoryQualityWebsiteSearchTerm.value ? false : true;
    this.getInventoryQualityData(0, includeCategories);
  }

  get showRibbonsAndBanner(){
    return (!this.account() || this.filters && this.filters?.campaigns?.length === 0) as boolean;
  }

}
